/* eslint-disable react-refresh/only-export-components */
import {
  DimeButton,
  DimeField,
  DimeFieldGroup,
  DimeFieldLabel,
  DimeInput,
  DimeSeparator,
  DimeStepper,
  DimeWizard,
  DimeWizardContent,
  DimeWizardFooter,
  DimeWizardHeader,
  DimeWizardStepper,
  type DimeWizardStep,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/wizard')({
  component: WizardDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="rounded-lg border border-border bg-background p-6">{children}</div>;
}

const STEPS: DimeWizardStep[] = [
  { id: 'basics', title: 'Basics', subtitle: 'Number, type, UOM', status: 'complete' },
  { id: 'commercial', title: 'Commercial', subtitle: 'Product and vendor', status: 'current' },
  { id: 'stock', title: 'Stock & Cost', subtitle: 'Warehouse and cost', status: 'upcoming' },
];

function BasicExample() {
  const [selected, setSelected] = React.useState<string | null>(null);

  return (
    <DimeWizard aria-label="Create part">
      <DimeWizardHeader>
        <DimeWizardStepper steps={STEPS} onStepSelect={setSelected} />
      </DimeWizardHeader>
      <DimeWizardContent>
        <DimeFieldGroup className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DimeField>
            <DimeFieldLabel htmlFor="wiz-first">First Name</DimeFieldLabel>
            <DimeInput id="wiz-first" defaultValue="Sunil" />
          </DimeField>
          <DimeField>
            <DimeFieldLabel htmlFor="wiz-last">Last Name</DimeFieldLabel>
            <DimeInput id="wiz-last" defaultValue="Joshi" />
          </DimeField>
          <DimeField className="sm:col-span-2">
            <DimeFieldLabel htmlFor="wiz-address">Address</DimeFieldLabel>
            <DimeInput id="wiz-address" defaultValue="123 Main St, Apt 4B" />
          </DimeField>
        </DimeFieldGroup>
      </DimeWizardContent>
      <DimeWizardFooter>
        <DimeButton variant="outline">Back</DimeButton>
        <DimeButton>Continue</DimeButton>
      </DimeWizardFooter>
      {selected && <p className="px-6 pb-4 text-sm text-muted-foreground">Jumped to {selected}</p>}
    </DimeWizard>
  );
}

function IssuesExample() {
  return (
    <DimeWizardStepper
      steps={[
        { id: 'basics', title: 'Basics', status: 'issues', issueCount: 2 },
        {
          id: 'commercial',
          title: 'Commercial',
          subtitle: 'Product and vendor',
          status: 'current',
        },
        { id: 'stock', title: 'Stock & Cost', status: 'upcoming' },
      ]}
    />
  );
}

const FREE_STEPS = [
  { id: 'details', title: 'Details' },
  { id: 'fields', title: 'Fields', badge: '2' },
  { id: 'review', title: 'Review', disabled: true },
];

function FreeNavigationExample() {
  const [current, setCurrent] = React.useState('details');
  return <DimeStepper steps={FREE_STEPS} currentStepId={current} onStepSelect={setCurrent} />;
}

function WizardDocs() {
  return (
    <div className="mx-auto max-w-4xl py-8">
      <h1 className="mb-6 text-3xl font-bold">Wizard</h1>
      <DocsSection title="Card, stepper, and footer">
        <Preview>
          <BasicExample />
        </Preview>
      </DocsSection>
      <DocsSection title="Free-navigation stepper">
        <Preview>
          <FreeNavigationExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeStepper</code> lets people jump to any step that is not{' '}
          <code className="font-mono">disabled</code>, with an optional{' '}
          <code className="font-mono">badge</code> per step. Use{' '}
          <code className="font-mono">DimeWizardStepper</code> when steps carry a status and issue
          count.
        </p>
      </DocsSection>

      <DocsSection title="Compact stepper">
        <Preview>
          <DimeWizardStepper compact steps={STEPS} />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">compact</code> shows the current step and a count, for narrow
          screens.
        </p>
      </DocsSection>

      <DocsSection title="A step that failed validation">
        <Preview>
          <IssuesExample />
        </Preview>
      </DocsSection>
    </div>
  );
}
